import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Check,
  ClipboardCopy,
  LayoutGrid,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Type,
  type LucideIcon,
} from 'lucide-react'
import type { ShiftDashboardView, ShiftTileSummary } from '@/api/shiftDashboardApi'
import { PageHeading } from '@/components/layout/PageHeading'
import { usePageTitle } from '@/components/layout/PageTitle'
import { AttentionPanel } from '@/components/shift-dashboard/AttentionPanel'
import { ShiftBand } from '@/components/shift-dashboard/ShiftBand'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AddShiftWidgetsDialog } from '@/components/shift-dashboard/AddShiftWidgetsDialog'
import { ShiftDrilldown } from '@/components/shift-dashboard/ShiftDrilldown'
import { ShiftFilterBar } from '@/components/shift-dashboard/ShiftFilterBar'
import { ShiftNotesWidget } from '@/components/shift-dashboard/ShiftNotesWidget'
import { ShiftTileGrid, ShiftTileStrip } from '@/components/shift-dashboard/ShiftTiles'
import { ShiftViewNameDialog } from '@/components/shift-dashboard/ShiftViewNameDialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  useShiftBoard,
  useShiftFilterOptions,
  useShiftNotes,
  useShiftTile,
  useShiftViewMutations,
  useShiftViews,
} from '@/hooks/useShiftDashboard'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  filtersDifferFromView,
  filtersFromParams,
  filtersToParams,
  reconcileFilters,
  scopeParts,
  setFilter,
  viewFilters,
  viewPayload,
  viewTiles,
  type ShiftFilters,
} from '@/lib/shiftDashboard'
import { handoverText } from '@/lib/shiftExport'
import { tileIdsToWidgets } from '@/lib/shiftTiles'
import { cn } from '@/lib/utils'
import { isoDay } from '@/lib/taskDateRange'

/**
 * The Shift Dashboard (z8nrz7fbcj): its own page, reached from "Shift
 * Dashboard" in the header beside Audit.
 *
 * Expanded, it is a grid of tiles — a count, a status line and a status chip
 * each — with "Attention this shift" and Shift notes on the right. Opening a
 * tile collapses every tile into one sideways-scrolling row, keeps their
 * order, highlights the open one, and shows its rows and their detail below.
 * Expand dashboard brings the grid back. A tile's count is the length of the
 * very rows its drill-down lists: the server computes both in one place.
 *
 * A view is a NAMED SHARED setting for the tenant — its filters and its tiles
 * — not a personal layout. Anyone with shift_dashboard.view opens views and
 * narrows the filters for their own visit; only shift_dashboard.manage
 * creates, renames, re-saves and deletes them. Without any view the page
 * still works, on the default tiles.
 *
 * Everything on screen rides in the URL (?view=&tile=&site=&room=&date=&shift=&team=&group=),
 * so a link hands over exactly the shift — and the tile — being looked at.
 */
export function ShiftDashboardPage() {
  usePageTitle('Shift Dashboard')
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.shiftDashboardManage)
  const canWriteNotes = can(PERMISSIONS.shiftDashboardNotes)
  const canManageTeams = can(PERMISSIONS.resourceCapabilitiesManage)

  const options = useShiftFilterOptions()
  const views = useShiftViews()
  const mutations = useShiftViewMutations()

  const [params, setParams] = useSearchParams()
  const today = isoDay(new Date())
  const viewId = params.get('view')
  const list = views.data ?? []
  const active = list.find((v) => v.id === viewId) ?? null

  const raw = filtersFromParams(params, today)
  // A link can name a room, team or group that has since gone; show what exists.
  const filters = options.data ? reconcileFilters(raw, options.data) : raw

  // No view in the URL (or one that was deleted): open the first, the way the
  // Dashboards page opens its default. Replace, so Back leaves the page.
  useEffect(() => {
    // Not mid-refetch: a view just created is in the URL before it is in the list.
    if (!views.data || views.data.length === 0 || views.isFetching) return
    if (viewId && views.data.some((v) => v.id === viewId)) return
    openView(views.data[0], true)
  }, [views.data, views.isFetching, viewId])

  function openView(view: ShiftDashboardView, replace = false) {
    const next = new URLSearchParams(params)
    next.set('view', view.id)
    next.delete('tile')
    setParams(filtersToParams({ ...viewFilters(view), date: filters.date }, next, today), { replace })
  }

  const changeFilter = (key: keyof ShiftFilters, value: string | null) => {
    if (!options.data) return
    setParams(filtersToParams(setFilter(filters, key, value, options.data), params, today), { replace: true })
  }

  // ── The board ──
  const query = {
    date: filters.date,
    shiftDefinitionId: filters.shiftDefinitionId,
    siteId: filters.siteId,
    roomId: filters.roomId,
    teamId: filters.teamId,
    groupId: filters.groupId,
  }
  const board = useShiftBoard(query)
  const tiles = useMemo(
    () => new Map<string, ShiftTileSummary>((board.data?.tiles ?? []).map((t) => [t.id, t])),
    [board.data],
  )
  const tileIds = viewTiles(active)

  // The open tile rides in the URL; one the view does not show is ignored.
  const openTileId = params.get('tile')
  const openTile = openTileId && tileIds.includes(openTileId) ? openTileId : null
  const detail = useShiftTile(openTile, query)
  const setOpenTile = (id: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (id) next.set('tile', id)
        else next.delete('tile')
        return next
      },
      { replace: !!openTile && !!id },
    )

  // ── Edit layout: add, remove and reorder widgets on the open view ──
  const [draft, setDraft] = useState<string[] | null>(null)
  const [adding, setAdding] = useState(false)
  const editing = draft !== null
  // A draft belongs to the view it was started on. Back/Forward or a tenant
  // switch changes the view without openView, and saving then would write one
  // view's tiles over another's for everyone.
  useEffect(() => {
    setDraft(null)
    setAdding(false)
  }, [active?.id])
  const startEditing = () => {
    setOpenTile(null)
    setDraft(tileIds)
  }
  const cancelEditing = () => {
    setDraft(null)
    setAdding(false)
  }
  const shownIds = draft ?? tileIds

  // ── Refresh and handover ──
  const notes = useShiftNotes({
    date: filters.date,
    shiftDefinitionId: filters.shiftDefinitionId,
    siteId: filters.siteId,
    teamId: filters.teamId,
  })
  const [copied, setCopied] = useState(false)
  const copyHandover = async () => {
    if (!board.data) return
    const text = handoverText({
      window: board.data.window,
      tiles: shownIds.map((id) => tiles.get(id)).filter((t): t is ShiftTileSummary => !!t),
      attention: board.data.attention,
      attentionTotal: board.data.attentionTotal,
      notes: notes.data ?? [],
    })
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setActionError('Could not copy to the clipboard. Allow clipboard access for this site and try again.')
    }
  }
  const updatedAt = board.dataUpdatedAt
    ? new Date(board.dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null

  // ── Naming, saving, deleting ──
  const [naming, setNaming] = useState<'new' | 'rename' | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const saving = mutations.create.isPending || mutations.update.isPending || mutations.remove.isPending

  const submitName = (name: string) => {
    setNameError(null)
    if (naming === 'rename' && active) {
      mutations.update.mutate(
        { id: active.id, body: { name } },
        { onSuccess: () => setNaming(null), onError: (e) => setNameError(e.message) },
      )
      return
    }
    // A new view starts from what is on screen: these filters, these tiles.
    mutations.create.mutate(viewPayload(name, filters, tileIds), {
      onSuccess: (created) => {
        setNaming(null)
        openView(created)
      },
      onError: (e) => setNameError(e.message),
    })
  }

  const saveFilters = () => {
    if (!active) return
    setActionError(null)
    // Filters only: the name and tiles are left as they are.
    const { siteId, roomId, shiftDefinitionId, teamId, groupId } = viewPayload(active.name, filters, tileIds)
    const body = { siteId, roomId, shiftDefinitionId, teamId, groupId }
    mutations.update.mutate({ id: active.id, body }, { onError: (e) => setActionError(e.message) })
  }

  const saveTiles = () => {
    if (!active || !draft) return
    setActionError(null)
    mutations.update.mutate(
      { id: active.id, body: { widgets: tileIdsToWidgets(draft) } },
      { onSuccess: cancelEditing, onError: (e) => setActionError(e.message) },
    )
  }

  const removeView = () => {
    if (!active) return
    setActionError(null)
    const removedId = active.id
    mutations.remove.mutate(removedId, {
      onSuccess: () => {
        setDeleting(false)
        // Functional: the list has refetched by now and may already have moved the URL on.
        setParams(
          (p) => {
            const next = new URLSearchParams(p)
            if (next.get('view') === removedId) next.delete('view')
            return next
          },
          { replace: true },
        )
      },
      onError: (e) => {
        setDeleting(false)
        setActionError(e.message)
      },
    })
  }

  const [menuOpen, setMenuOpen] = useState(false)

  // Site, room, shift, team and group go; the day stays.
  const clearFilters = () =>
    setParams(
      filtersToParams(
        { date: filters.date, siteId: null, roomId: null, shiftDefinitionId: null, teamId: null, groupId: null },
        params,
        today,
      ),
      { replace: true },
    )

  const filtersChanged = active ? filtersDifferFromView(filters, active) : false
  const error = actionError ?? (options.error ?? views.error ?? board.error)?.message

  return (
    <div className='flex h-full flex-col gap-4 overflow-auto bg-muted/30 px-4 py-5 sm:px-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <PageHeading title='Shift Dashboard' />

        {/* Views: which shared view is open, and (for managers) what to do with it. */}
        <div className='flex flex-wrap items-center gap-2'>
          {list.length > 0 ? (
            <Select
              value={active?.id ?? ''}
              onValueChange={(id) => {
                const v = list.find((x) => x.id === id)
                if (v) openView(v)
              }}
            >
              <SelectTrigger className='h-9 w-60 bg-background' aria-label='Shift view'>
                <SelectValue placeholder='Choose a view' />
              </SelectTrigger>
              <SelectContent>
                {list.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            views.data && (
              <span className='text-sm text-muted-foreground'>
                {canManage ? 'No saved views yet, so every tile is shown.' : 'No saved views yet. A supervisor can set them up.'}
              </span>
            )
          )}
          {canManage && (
            <>
              {active && filtersChanged && (
                <Button
                  size='sm'
                  onClick={saveFilters}
                  disabled={saving}
                  title={`Save these filters to "${active.name}" for everyone`}
                >
                  <Save className='mr-1.5 h-3.5 w-3.5' /> Save filters to view
                </Button>
              )}
              {active && !editing && (
                <Button size='sm' variant='outline' className='bg-background' onClick={startEditing}>
                  <Pencil className='mr-1.5 h-3.5 w-3.5' /> Edit layout
                </Button>
              )}
              <Button
                size='sm'
                variant='outline'
                className='bg-background'
                onClick={() => {
                  setNameError(null)
                  setNaming('new')
                }}
                disabled={!views.data || editing}
              >
                <Plus className='mr-1.5 h-3.5 w-3.5' /> New view
              </Button>
              {active && (
                <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                  <PopoverTrigger asChild>
                    <Button size='sm' variant='outline' className='bg-background' aria-label='View options'>
                      <MoreHorizontal className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align='end' className='w-52 p-1'>
                    <MenuItem icon={Pencil} onClick={() => { setMenuOpen(false); startEditing() }}>
                      Edit layout
                    </MenuItem>
                    <MenuItem icon={Type} onClick={() => { setMenuOpen(false); setNameError(null); setNaming('rename') }}>
                      Rename view
                    </MenuItem>
                    <div className='my-1 h-px bg-border' />
                    <MenuItem icon={Trash2} destructive onClick={() => { setMenuOpen(false); setDeleting(true) }}>
                      Delete view
                    </MenuItem>
                  </PopoverContent>
                </Popover>
              )}
            </>
          )}
        </div>
      </div>

      <ShiftBand window={board.data?.window} />

      {/* Freshness and handover: the board refreshes itself every minute; this says when it last did. */}
      <div className='-mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500'>
        <span className='flex items-center gap-2'>
          {updatedAt && <span>Updated {updatedAt}</span>}
          <button
            type='button'
            onClick={() => board.refetch()}
            disabled={board.isFetching}
            className='inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-medium text-gray-600 hover:bg-white hover:text-blue-600 disabled:opacity-60'
          >
            <RefreshCw aria-hidden className={cn('h-3.5 w-3.5', board.isFetching && 'animate-spin motion-reduce:animate-none')} />
            {board.isFetching ? 'Refreshing' : 'Refresh'}
          </button>
        </span>
        <button
          type='button'
          onClick={copyHandover}
          disabled={!board.data}
          title='Copy the flagged tiles, the Attention list and the open notes as text'
          className='inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 font-medium text-gray-600 hover:bg-white hover:text-blue-600 disabled:opacity-60'
        >
          {copied ? <Check aria-hidden className='h-3.5 w-3.5 text-green-600' /> : <ClipboardCopy aria-hidden className='h-3.5 w-3.5' />}
          <span aria-live='polite'>{copied ? 'Handover copied' : 'Copy handover'}</span>
        </button>
      </div>

      <ShiftFilterBar
        filters={filters}
        options={options.data}
        onChange={changeFilter}
        onClear={clearFilters}
        canManageTeams={canManageTeams}
      />

      {error && (
        <p role='alert' className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
          {error}
        </p>
      )}

      {openTile ? (
        <div className='space-y-4'>
          <div className='flex items-center gap-3'>
            <Button size='sm' variant='outline' className='shrink-0 bg-background' onClick={() => setOpenTile(null)}>
              <LayoutGrid className='mr-1.5 h-3.5 w-3.5' /> All tiles
            </Button>
            <div className='min-w-0 flex-1'>
              <ShiftTileStrip ids={tileIds} tiles={tiles} selected={openTile} onSelect={(id) => setOpenTile(id)} />
            </div>
          </div>
          <ShiftDrilldown
            detail={detail.data}
            loading={detail.isLoading}
            error={detail.error}
            scope={scopeParts(filters, options.data)}
            date={filters.date}
          />
        </div>
      ) : (
        <div className='flex flex-col gap-4 xl:flex-row xl:items-start'>
          <div className='min-w-0 flex-1 space-y-3'>
            {editing && active && (
              <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5'>
                <p className='text-sm text-blue-900'>
                  Editing the layout of <span className='font-semibold'>{active.name}</span>. Drag to reorder, − to remove.
                  Saving changes it for everyone.
                </p>
                <div className='flex items-center gap-2'>
                  <Button size='sm' variant='outline' className='bg-white' onClick={() => setAdding(true)}>
                    <Plus className='mr-1.5 h-3.5 w-3.5' /> Add widget
                  </Button>
                  <Button size='sm' variant='ghost' onClick={cancelEditing}>
                    Cancel
                  </Button>
                  <Button size='sm' onClick={saveTiles} disabled={saving || shownIds.length === 0}>
                    Save layout
                  </Button>
                </div>
              </div>
            )}
            {shownIds.length > 0 || editing ? (
              <ShiftTileGrid
                ids={shownIds}
                tiles={tiles}
                loading={board.isLoading}
                onOpen={(id) => setOpenTile(id)}
                editing={editing}
                onChange={setDraft}
                onAdd={() => setAdding(true)}
              />
            ) : (
              <div className='flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-background py-12 text-center'>
                <LayoutGrid aria-hidden className='h-7 w-7 text-muted-foreground/50' />
                <p className='text-sm text-muted-foreground'>This view has no widgets. Use Edit layout to add some.</p>
              </div>
            )}
          </div>
          {/* Sticky beside the tiles on wide screens; each panel scrolls inside itself. */}
          <div className='flex w-full shrink-0 flex-col gap-4 xl:sticky xl:top-0 xl:w-[22rem]'>
            <AttentionPanel
              items={board.data?.attention ?? []}
              total={board.data?.attentionTotal ?? 0}
              loading={board.isLoading}
              onOpenTile={(id) => tileIds.includes(id) && setOpenTile(id)}
            />
            <ShiftNotesWidget
              date={filters.date}
              shiftDefinitionId={filters.shiftDefinitionId}
              siteId={filters.siteId}
              teamId={filters.teamId}
              canWrite={canWriteNotes}
            />
          </div>
        </div>
      )}

      {draft && (
        <AddShiftWidgetsDialog open={adding} onOpenChange={setAdding} draft={draft} onChange={setDraft} />
      )}

      <ShiftViewNameDialog
        open={naming !== null}
        onOpenChange={(open) => !open && setNaming(null)}
        views={list}
        editing={naming === 'rename' ? active : null}
        saving={saving}
        error={nameError}
        onSubmit={submitName}
      />

      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Delete "{active?.name}"?</DialogTitle>
            <DialogDescription>
              The view is removed for everyone in this tenant. The data behind its tiles is not touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='ghost' onClick={() => setDeleting(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={removeView} disabled={saving}>
              Delete view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function MenuItem({
  icon: Icon,
  destructive,
  onClick,
  children,
}: {
  icon: LucideIcon
  destructive?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
        destructive && 'text-destructive',
      )}
    >
      <Icon aria-hidden className='h-4 w-4' />
      {children}
    </button>
  )
}
